import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Select } from "antd";
import { useFetchGetMe } from "hooks/usersHooks";
import SidebarAnalyticsNew from "./SidebarAnalyticsNew";
import { useTranslation } from "react-i18next";
import * as motion from "motion/react-client";
import { can } from "utils/access";
import { DIRECTION_ROLES, FACULTY_ROLES, RECTOR_ROLES } from "utils/roles";

interface SelectOptionType {
  label: string;
  value: string;
}

const langOptions: Array<SelectOptionType> = [
  { label: "Рус", value: "ru" },
  { label: "Uzb", value: "uz" },
  { label: "Eng", value: "en" },
];

const normalizeLang = (lng?: string) => {
  if (!lng) return "uz";
  const base = lng.toLowerCase().split("-")[0];
  if (base === "ru" || base === "uz" || base === "en") return base;
  return "uz";
};

type SidebarProps = {
  /** Normalized lowercase role names from ProtectedRoute */
  userRole: string | string[];
  /** Mobile drawer state (desktop rail ignores it). */
  mobileOpen?: boolean;
  onClose?: () => void;
};

const Sidebar = ({ userRole, mobileOpen = false, onClose }: SidebarProps) => {
  const { t, i18n } = useTranslation();
  const { data: userItem } = useFetchGetMe({ enabled: true });
  const navigate = useNavigate();
  const location = useLocation();

  // console.log(userItem);

  const [openLocation, setOpenLocation] = useState(false);

  const isActive = (path: string) =>
    location.pathname === path ? "active-menu" : "";

  const roleList = Array.isArray(userRole)
    ? userRole
    : userRole
      ? [String(userRole).toLowerCase()]
      : [];
  const isAdmin = roleList.includes("admin");
  const isSuperAdmin = roleList.includes("super_admin");
  const isRektor = roleList.some((r) => RECTOR_ROLES.includes(r));
  const isRestrictedOther =
    !!userItem?.access?.restricted &&
    !roleList.some((r) => [...RECTOR_ROLES, ...DIRECTION_ROLES, "admin", "super_admin"].includes(r));
  const isFacultyAdmin = roleList.some((r) => FACULTY_ROLES.includes(r)) || isRestrictedOther;
  const isDirectionAdmin = roleList.some((r) => DIRECTION_ROLES.includes(r));
  const canManageAccess = can(userItem, "view_user-access");
  const canManageRoles = can(userItem, "view_roles");
  const isTeacher = roleList.includes("teacher");
  const isStudent = roleList.includes("student");
  const [openImages, setOpenImages] = useState(false);
  const [openExams, setOpenExams] = useState(false);
  // Rail is opened by hover (or keyboard focus) and collapses after a menu click.
  const [railOpen, setRailOpen] = useState(false);
  const [lang, setLang] = useState<string>(normalizeLang(i18n.language));

  useEffect(() => {
    setLang(normalizeLang(i18n.language));
  }, [i18n.language]);

  // На мобильных меню закрывается после перехода по ссылке.
  useEffect(() => {
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // При сворачивании rail-меню раскрытые разделы схлопываются.
  const collapseSections = () => {
    setOpenImages(false);
    setOpenLocation(false);
    setOpenExams(false);
  };

  useEffect(() => {
    if (!mobileOpen) collapseSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileOpen]);

  const handleChangeLanguage = (val: string) => {
    i18n.changeLanguage(val);
    setLang(val);
  };

  /** A menu link click collapses the rail even while the pointer is over it. */
  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest("a");
    if (!link) return;

    setRailOpen(false);
    collapseSections();
    link.blur();
    onClose?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: 0 }}
      className={`main-side-bar${mobileOpen ? " main-side-bar--mobile-open" : ""}${
        railOpen ? " main-side-bar--rail-open" : ""
      }`}
      onClick={handleMenuClick}
    >
      <div
        className="my-side-fix"
        onMouseEnter={() => setRailOpen(true)}
        onMouseLeave={() => {
          setRailOpen(false);
          collapseSections();
        }}
      >
        {/* LOGO */}
        <div className="top-logo">
          <Link to="/">
            <img src="/logo.svg" alt="logo" className="logo-company" />
          </Link>
        </div>

        <div className="mid-side">
          <div className="mid-side-top">
            {/* ---------------- NEW ANALYTICS ---------------- */}
            {(isRektor || isAdmin || isSuperAdmin || isFacultyAdmin || isDirectionAdmin) && (
              <SidebarAnalyticsNew />
            )}
            {(isRektor || isFacultyAdmin) && !(isAdmin || isSuperAdmin) && (
              <Link
                to="/courses"
                className={`link-item-style ${isActive("/courses")}`}
              >
                <span className="icon icon-checkdone" />
                <span className="menu-item-style font-family-openSans-SemiBold">
                  {t("common.sidebar.lessonAttendance")}
                </span>
              </Link>
            )}
            {(isAdmin || isSuperAdmin) && (
              <>
                <div
                  className={`link-item-style d-flex justify-content-between align-items-center ${openImages ? "active-menu" : ""
                    }`}
                  onClick={() => setOpenImages(!openImages)}
                >
                  <span>
                    <span className="icon icon-image" />
                    <span className="menu-item-style font-family-openSans-SemiBold">
                      {t("common.sidebar.images")}
                    </span>
                  </span>
                  <span
                    className={`icon ${openImages
                      ? "icon-chevronup"
                      : "icon-chevronup icon-chevrondown"
                      }`}
                  />
                </div>

                {openImages && (
                  <div>
                    <NavLink
                      to="/uploads"
                      className="link-item-style-child"
                    >
                      {t("common.sidebar.uploadImage")}
                    </NavLink>

                    <NavLink
                      to="/no-image-faculties"
                      className="link-item-style-child"
                    >
                      {t("common.sidebar.imagesList")}
                    </NavLink>
                  </div>
                )}
                {isSuperAdmin ? (
                  <NavLink
                    to="/hemis-sync"
                    className={`link-item-style ${isActive("/hemis-sync")}`}
                  >
                    <span className="icon icon-refresh" />
                    <span className="menu-item-style font-family-openSans-SemiBold">
                      HEMIS Sync
                    </span>
                  </NavLink>
                ) : null}
                <div
                  className={`link-item-style d-flex justify-content-between align-items-center ${openLocation ? "active-menu" : ""
                    }`}
                  onClick={() => setOpenLocation(!openLocation)}
                >
                  <span>
                    <span className="icon icon-room" />
                    <span className="menu-item-style font-family-openSans-SemiBold">
                      {t("common.sidebar.location")}
                    </span>
                  </span>
                  <span
                    className={`icon ${openLocation
                      ? "icon-chevronup"
                      : "icon-chevronup icon-chevrondown"
                      }`}
                  />
                </div>
                {openLocation && (
                  <div>

                    <NavLink
                      to="/location/list"
                      className="link-item-style-child"
                    >
                      {t("common.sidebar.locationsList")}
                    </NavLink>
                    <NavLink
                      to="/location/connect/list"
                      className="link-item-style-child"
                    >
                      {t("common.sidebar.practices")}                   
                       </NavLink>
                        <NavLink
                      to="/practical-schedules-attandance"
                      className="link-item-style-child"
                    >
                      {t("common.sidebar.practicesAttendance")}                  
                       </NavLink>
                  </div>

                )}

                <div
                  className={`link-item-style d-flex justify-content-between align-items-center ${openExams ? "active-menu" : ""
                    }`}
                  onClick={() => setOpenExams(!openExams)}
                >
                  <span>
                    <span className="icon icon-shapka" />
                    <span className="menu-item-style font-family-openSans-SemiBold">
                      {t("common.sidebar.exams")}
                    </span>
                  </span>
                  <span
                    className={`icon ${openExams
                      ? "icon-chevronup"
                      : "icon-chevronup icon-chevrondown"
                      }`}
                  />
                </div>

                {openExams && (
                  <div>
                    <NavLink
                      to="/exams/"
                      className={({ isActive }) =>
                        "link-item-style-child" + (isActive ? " active" : "")
                      }
                      end
                    >
                      {t("common.sidebar.examsSchedule")}
                    </NavLink>
                    <NavLink
                      to="/exams/analytics"
                      className={({ isActive }) =>
                        "link-item-style-child" + (isActive ? " active" : "")
                      }
                    >
                      {t("common.sidebar.examsAttendance")}
                    </NavLink>
                  </div>
                )}
                <Link
                  to="/courses"
                  className={`link-item-style ${isActive("/courses")}`}
                >
                  <span className="icon icon-checkdone" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.sidebar.lessonAttendance")}
                  </span>
                </Link>
                <Link
                  to="/cameras"
                  className={`link-item-style ${isActive("/cameras")}`}
                >
                  <span className="icon icon-webcam" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.cameras.cameras")}
                  </span>
                </Link>


                <Link
                  to="/class-schedule"
                  className={`link-item-style ${isActive("/class-schedule")}`}
                >
                  <span className="icon icon-calendar" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.classSchedule")}
                  </span>
                </Link>

                <Link
                  to="/university/buildings/1"
                  className={`link-item-style ${isActive(
                    "/university/buildings/1"
                  )}`}
                >
                  <span className="icon icon-buildings" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.buildings")}
                  </span>
                </Link>
                <Link
                  to="/roleUsers"
                  className={`link-item-style ${isActive("/roleUsers")}`}
                >
                  <span className="icon icon-users" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.sidebar.userManagement")}
                  </span>
                </Link>
                <Link
                  to="/users/add-roles"
                  className={`link-item-style ${isActive("/users/add-roles")}`}
                >
                  <span className="icon icon-settings" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.roleManagement")}
                  </span>
                </Link>
                <Link
                  to="/roi"
                  className={`link-item-style ${isActive("/roi")}`}
                >
                  <span className="icon icon-roi" />
                  <span className="menu-item-style font-family-openSans-SemiBold">
                    {t("common.sidebar.roi")}
                  </span>
                </Link>


              </>
            )}

            {!(isAdmin || isSuperAdmin) && canManageAccess && (
              <Link
                to="/roleUsers"
                className={`link-item-style ${isActive("/roleUsers")}`}
              >
                <span className="icon icon-users" />
                <span className="menu-item-style font-family-openSans-SemiBold">
                  {t("common.sidebar.userManagement")}
                </span>
              </Link>
            )}
            {!(isAdmin || isSuperAdmin) && canManageRoles && (
              <Link
                to="/users/add-roles"
                className={`link-item-style ${isActive("/users/add-roles")}`}
              >
                <span className="icon icon-settings" />
                <span className="menu-item-style font-family-openSans-SemiBold">
                  {t("common.roleManagement")}
                </span>
              </Link>
            )}

            {(isTeacher || isStudent) && (
              <Link
                to="/class-schedule"
                className={`link-item-style ${isActive("/class-schedule")}`}
              >
                <span className="icon icon-calendar" />
                <span className="menu-item-style font-family-openSans-SemiBold">
                  {t("common.classSchedule")}
                </span>
              </Link>
            )}
          </div>

          {/* ---------------- PROFILE ---------------- */}
          <div className="mid-side-top">
            <span className="lang-badge" title={lang.toUpperCase()}>
              {lang.toUpperCase()}
            </span>
            <Select
              className="lang-switch"
              popupClassName="lang-switch-dropdown"
              options={langOptions}
              style={{ width: "100%" }}
              value={lang}
              onChange={handleChangeLanguage}
            />

            <div className="acc-profile">
              <div
                className="d-flex cursor-pointer"
                onClick={() => {
                  onClose?.();
                  navigate("/profile");
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    onClose?.();
                    navigate("/profile");
                  }
                }}
              >
                <div className="acc-img">
                  <img src="/img/adminUser.png" alt="user" width="40" />
                </div>
                <div className="acc-content">
                  <h5 className="font-family-openSans-SemiBold">
                    {userItem?.username}
                  </h5>
                  <h6 className="font-family-openSans-Regular">
                    {userItem?.user?.profile_type}
                  </h6>
                </div>
              </div>

              <button
                className="acc-toggle"
                onClick={() => {
                  localStorage.removeItem("token");
                  localStorage.removeItem("user_id");
                  navigate("/login");
                }}
              >
                <span className="icon icon-logout" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;
